import {useTranslation} from 'react-i18next'
import {create} from 'zustand'

import {DialogNew} from '@sdox/ui'
import {getOptions as getCookieOptions} from '@sdox/utils/dom'

import {CookieConsentContent} from './DialogContent/CookieConsentContent'
import {CookiePolicyContent} from './DialogContent/CookiePolicyContent'
import {PrivacyPolicyContent} from './DialogContent/PrivacyPolicyContent'
import {CookieSettingsContent} from './DialogContent/CookieSettingsContent'

export enum DialogContent {
  Closed = 'closed',
  Initial = 'initial',
  CookiePolicy = 'cookie-policy',
  CookieSettings = 'cookie-settings',
  PrivacyPolicy = 'privacy-policy',
}

export type FirstVisitDialogState = {
  selectedContent: DialogContent
  selectDialogContent: (contentType: DialogContent) => void
  closeDialog: () => void
}

export const useFirstVisitDialogStore = create<FirstVisitDialogState>((set) => ({
  selectedContent: getCookieOptions() !== null ? DialogContent.Closed : DialogContent.Initial,
  selectDialogContent: (selectedContent: DialogContent) => set({selectedContent}),
  closeDialog: () => set({selectedContent: DialogContent.Closed}),
}))

export const FirstVisitDialog = () => {
  const {t} = useTranslation()
  const selectedContent = useFirstVisitDialogStore(state => state.selectedContent)
  const selectDialogContent = useFirstVisitDialogStore(state => state.selectDialogContent)
  const closeDialog = useFirstVisitDialogStore(state => state.closeDialog)
  const isOpen = selectedContent !== DialogContent.Closed

  const openInitial = () => {
    selectDialogContent(DialogContent.Initial)
  }

  const onCookieSettingsClose = ({didSave}: {didSave: boolean}) => {
    if (didSave) {
      selectDialogContent(DialogContent.Closed)
    } else {
      selectDialogContent(DialogContent.Initial)
    }
  }

  let heading: string
  switch (selectedContent) {
    case DialogContent.Initial:
      heading = t('common:cookieAndPrivacySettings')
      break
    case DialogContent.CookieSettings:
      heading = t('common:cookiesSettings')
      break
    case DialogContent.CookiePolicy:
      heading = t('common:cookiesPolicy')
      break
    case DialogContent.PrivacyPolicy:
      heading = t('common:privacyPolicy')
      break
    default:
      heading = ''
  }

  return (
    <DialogNew
      open={isOpen}
      onClose={closeDialog}
      heading={heading}
      persistent={true}
    >
      {selectedContent === DialogContent.Initial && (
        <CookieConsentContent />
      )}
      {selectedContent === DialogContent.CookieSettings && (
        <CookieSettingsContent onClose={onCookieSettingsClose} />
      )}
      {selectedContent === DialogContent.CookiePolicy && (
        <CookiePolicyContent onClose={openInitial} />
      )}
      {selectedContent === DialogContent.PrivacyPolicy && (
        <PrivacyPolicyContent onClose={openInitial} />
      )}
    </DialogNew>
  )
}
